import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ProtectedRoute, PublicOnlyRoute } from '@/components/RouteGuards';
import LoginPage from '@/pages/auth/LoginPage';
import ChangePasswordPage from '@/pages/auth/ChangePasswordPage';
import UsersPage from '@/pages/settings/UsersPage';
import TvSignInsPage from '@/pages/settings/TvSignInsPage';
import UserDetailPage from '@/pages/settings/UserDetailPage';
import PermissionsExplorerPage from '@/pages/settings/PermissionsExplorerPage';
import AutoDetectedTripsPage from '@/pages/trips/AutoDetectedTripsPage';
import TripDetectionSettingsPage from '@/pages/settings/TripDetectionSettingsPage';
import DashboardPage from '@/pages/dashboard/DashboardPage';
import InvestorsPage from '@/pages/investors/InvestorsPage';
import RateConflictsPage from '@/pages/investors/RateConflictsPage';
import VehiclesPage from '@/pages/vehicles/VehiclesPage';
import FleetLivePage from '@/pages/fleet/FleetLivePage';
import ControlTowerPage from '@/pages/fleet/ControlTowerPage';
import FleetExplorerPage from '@/pages/fleet/FleetExplorerPage';
import VehicleImportPage from '@/pages/imports/VehicleImportPage';
import DriverImportPage from '@/pages/imports/DriverImportPage';
import BodiesPage from '@/pages/bodies/BodiesPage';
import DriversPage from '@/pages/drivers/DriversPage';
import ClientsPage from '@/pages/clients/ClientsPage';
import SitesPage from '@/pages/sites/SitesPage';
import ContractsPage from '@/pages/contracts/ContractsPage';
import PhotoAuditPage from '@/pages/audit/PhotoAuditPage';
import PaymentsPage from '@/pages/payments/PaymentsPage';
import TripsPage from '@/pages/trips/TripsPage';
import OcrReviewPage from '@/pages/ocr-review/OcrReviewPage';
import ExpensesPage from '@/pages/expenses/ExpensesPage';
import CardMovementsPage from '@/pages/fuel/CardMovementsPage';
import FuelImportWizard from '@/pages/fuel/FuelImportWizard';
import FuelPage from '@/pages/fuel/FuelPage';
import ConsumptionPage from '@/pages/fuel/ConsumptionPage';
import IdleWastePage from '@/pages/fuel/IdleWastePage';
import DriverLeaderboardPage from '@/pages/fuel/DriverLeaderboardPage';
import CoachingPage from '@/pages/fuel/CoachingPage';
import MonthlyReportsPage from '@/pages/fuel/MonthlyReportsPage';
import ExpiryDashboardPage from '@/pages/documents/ExpiryDashboardPage';
import PackBuilderPage from '@/pages/documents/PackBuilderPage'; // PR-DOCVAULT JUN03
import CompanyDocumentsPage from '@/pages/settings/CompanyDocumentsPage'; // PR-DOCVAULT JUN03
import OperationsLivePage from '@/pages/dashboard/OperationsLivePage';
import DailyClosingPage from '@/pages/closings/DailyClosingPage';
import ClosingsAdminPage from '@/pages/closings/ClosingsAdminPage';
import MaintenancePage from '@/pages/maintenance/MaintenancePage';
import VehicleHeatmapPage from '@/pages/analytics/VehicleHeatmapPage';
import ReportsHomePage from '@/pages/reports/ReportsHomePage';
import DailySiteReportPage from '@/pages/reports/DailySiteReportPage';
import ReportRecipientsPage from '@/pages/settings/ReportRecipientsPage';
import ActivityFeedPage from '@/pages/activity/ActivityFeedPage';
import CustomReportPage from '@/pages/reports/CustomReportPage';
import AfaqyAccountsPage from '@/pages/settings/AfaqyAccountsPage';
import FlatbedDashboardPage from '@/pages/flatbed/FlatbedDashboardPage';
import NewFlatbedTripPage from '@/pages/flatbed/NewFlatbedTripPage';
import ProjectsListPage from '@/pages/projects/ProjectsListPage';
import ProjectDetailPage from '@/pages/projects/ProjectDetailPage';
import TripRateCardsPage from '@/pages/settings/TripRateCardsPage';
import DriverAdvancesPage from '@/pages/cashflow/DriverAdvancesPage';
import DriverExpensesPage from '@/pages/cashflow/DriverExpensesPage';
import PetroAppPage from '@/pages/petroapp/PetroAppPage';
import OverdueCollectionsPage from '@/pages/cashflow/OverdueCollectionsPage';
// PR MAY10 P4C — maintenance module
import MaintenanceListPage from '@/pages/maintenance/MaintenanceListPage';
import MaintenanceEventCreatePage from '@/pages/maintenance/MaintenanceEventCreatePage';
import MaintenanceEventDetailPage from '@/pages/maintenance/MaintenanceEventDetailPage';
import { PendingVerificationPage, PendingApprovalPage, PendingPaymentPage } from '@/pages/maintenance/MaintenanceQueuePage';
import SparePartsCatalogPage from '@/pages/maintenance/SparePartsCatalogPage';
import InventoryPage from '@/pages/maintenance/InventoryPage';
import MaintenanceVendorsPage from '@/pages/maintenance/MaintenanceVendorsPage';
import ServiceSchedulePage from '@/pages/maintenance/ServiceSchedulePage';
import MaintenanceReportsPage from '@/pages/maintenance/MaintenanceReportsPage';
import FlatbedTripDetailPage from '@/pages/flatbed/FlatbedTripDetailPage';
import MyFlatbedTripsPage from '@/pages/flatbed/MyFlatbedTripsPage';
import DriverTripActionPage from '@/pages/flatbed/DriverTripActionPage';
import DriverAccountsPage from '@/pages/flatbed/DriverAccountsPage';
import DriverFinanceDetailPage from '@/pages/flatbed/DriverFinanceDetailPage';
import MonthlySalaryRunsPage from '@/pages/flatbed/MonthlySalaryRunsPage';
import CashFlowPage from '@/pages/flatbed/CashFlowPage';
import ClientPaymentTermsPage from '@/pages/flatbed/ClientPaymentTermsPage';
import MyAccountPage from '@/pages/flatbed/MyAccountPage';
import DriverPerformancePage from '@/pages/drivers/DriverPerformancePage';
import { NewVersionBanner } from '@/components/common/NewVersionBanner';
import AnomaliesPage from '@/pages/fuel/AnomaliesPage';
import LeaderboardPage from '@/pages/leaderboard/LeaderboardPage';
import WaterSuppliersPage from '@/pages/water/WaterSuppliersPage';
import WaterStationsPage from '@/pages/water/WaterStationsPage';
import ZohoSettingsPage from '@/pages/settings/ZohoSettingsPage';
import FleetPlusSettingsPage from '@/pages/settings/FleetPlusSettingsPage';
import OcrSettingsPage from '@/pages/ocr-review/OcrSettingsPage';
import ConstructionPage from '@/pages/construction/ConstructionPage';
import AttendanceFlowPage from '@/pages/construction/AttendanceFlowPage';
import VillaDetailPage from '@/pages/construction/VillaDetailPage';
import ExpensesListPage from '@/pages/construction/ExpensesListPage';
import PerformanceGalleryPage from '@/pages/construction/PerformanceGalleryPage';
import SupervisorsPage from '@/pages/construction/SupervisorsPage';
import FixedCostsPage from '@/pages/construction/FixedCostsPage';
// PR-STEP2-3 — three-tile home + global project box
import { CurrentProjectProvider } from '@/contexts/CurrentProjectContext';
import HomePage from '@/pages/home/HomePage';
// PR-STEP2-4 — cross-project owner view (directional)
import OwnerViewPage from '@/pages/owner/OwnerViewPage';
// PR-D1 — Dumpers project
import DumperClientsPage from '@/pages/dumpers/DumperClientsPage';
// PR-D2 — Dumpers trips
import DumperLogTripPage from '@/pages/dumpers/DumperLogTripPage';
import DumperTripsPage from '@/pages/dumpers/DumperTripsPage';
// PR-D4 — Dumpers driver pay + truck activity
import DumperDriverPayPage from '@/pages/dumpers/DumperDriverPayPage';
import DumperTruckActivityPage from '@/pages/dumpers/DumperTruckActivityPage';
// PR-D5 — Dumpers money layer
import DumperReceivablesPage from '@/pages/dumpers/DumperReceivablesPage';
import DumperInvoiceBuilderPage from '@/pages/dumpers/DumperInvoiceBuilderPage';
// PR-F1 (Step 4) — Flatbed cost-to-serve estimator
import FlatbedEstimatePage from '@/pages/flatbed/FlatbedEstimatePage';
// PR-F3 (Step 4) — Flatbed driver pay
import FlatbedDriverPayPage from '@/pages/flatbed/FlatbedDriverPayPage';
// PR-S5 (Step 5) — per-project cross-check reports
import ProjectReportsPage from '@/pages/reports/ProjectReportsPage';
// PR-A1 (Step A) — Settings hub
import SettingsHubPage from '@/pages/settings/SettingsHubPage';
import InspectionsPage from '@/pages/inspections/InspectionsPage';
// PR-A3 (Step A) — data quality list
import DataQualityPage from '@/pages/settings/DataQualityPage';
// PR-1 — Afaqy tracker health
import TrackerHealthPage from '@/pages/settings/TrackerHealthPage';
import VehicleHistoryPage from '@/pages/fleet/VehicleHistoryPage';
import UnreportedTripsPage from '@/pages/fleet/UnreportedTripsPage';
import SharedHistoryPage from '@/pages/public/SharedHistoryPage';
import ChangeRequestsPage from '@/pages/settings/ChangeRequestsPage';
import AuditLogPage from '@/pages/settings/AuditLogPage';
import DumperFuelPage from '@/pages/dumpers/DumperFuelPage';
import { SidesPage } from '@/pages/settings/SidesPage';
import VehicleConditionPage from '@/pages/vehicles/VehicleConditionPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

export default function App() {
  return (
    <ThemeProvider>
      {/* Outside the router: it belongs to the tab, not to any one page. */}
      <NewVersionBanner />
      <QueryClientProvider client={queryClient}>
        <CurrentProjectProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            {/* PR-STEP2-3 — three-tile project home */}
            <Route path="/home" element={
              <ProtectedRoute><HomePage /></ProtectedRoute>
            } />
            {/* PR-STEP2-4 — cross-project owner view (directional) */}
            <Route path="/dashboard/owner" element={
              <ProtectedRoute><OwnerViewPage /></ProtectedRoute>
            } />
            {/* PR-D1 — Dumpers clients & rates */}
            <Route path="/dashboard/dumpers/clients" element={
              <ProtectedRoute><DumperClientsPage /></ProtectedRoute>
            } />
            {/* PR-D2 — Dumpers loads */}
            <Route path="/dashboard/dumpers/log" element={
              <ProtectedRoute><DumperLogTripPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/dumpers/loads" element={
              <ProtectedRoute><DumperTripsPage /></ProtectedRoute>
            } />
            {/* PR-D4 — Dumpers driver pay + truck activity */}
            <Route path="/dashboard/dumpers/driver-pay" element={
              <ProtectedRoute><DumperDriverPayPage /></ProtectedRoute>
            } />
            {/* Same page, NEOM's trips. One component so a fix
                reaches both sides at once. */}
            <Route path="/dashboard/neom/driver-pay" element={
              <ProtectedRoute><DumperDriverPayPage side="neom" /></ProtectedRoute>
            } />
            <Route path="/dashboard/dumpers/trucks" element={
              <ProtectedRoute><DumperTruckActivityPage /></ProtectedRoute>
            } />
            {/* Dumper fuel: a truck per row, opening to its fills. */}
            <Route path="/dashboard/dumpers/fuel" element={
              <ProtectedRoute><DumperFuelPage /></ProtectedRoute>
            } />
            {/* PR-D5 — Dumpers money layer */}
            <Route path="/dashboard/dumpers/receivables" element={
              <ProtectedRoute><DumperReceivablesPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/dumpers/invoice-new" element={
              <ProtectedRoute><DumperInvoiceBuilderPage /></ProtectedRoute>
            } />
            <Route path="/login" element={
              <PublicOnlyRoute><LoginPage /></PublicOnlyRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute><DashboardPage /></ProtectedRoute>
            } />
            {/* PR-A1 — Settings hub (everything configuration in one place) */}
            <Route path="/dashboard/settings" element={
              <ProtectedRoute><SettingsHubPage /></ProtectedRoute>
            } />
            {/* PR-A3 — data quality */}
            <Route path="/dashboard/settings/data-quality" element={
              <ProtectedRoute><DataQualityPage /></ProtectedRoute>
            } />
            {/* PR-1 — Afaqy tracker health */}
            <Route path="/dashboard/settings/tracker-health" element={
              <ProtectedRoute><TrackerHealthPage /></ProtectedRoute>
            } />
            {/* PR-TRIP-AUDIT — where a truck really went, and the GPS trips
                that have no row in the operations records. */}
            <Route path="/dashboard/vehicle-history" element={
              <ProtectedRoute><VehicleHistoryPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/unreported-trips" element={
              <ProtectedRoute><UnreportedTripsPage /></ProtectedRoute>
            } />
            {/* Read-only, no login, expires in 7 days. Not a guarded route. */}
            <Route path="/shared/history/:token" element={<SharedHistoryPage />} />
            {/* Filing screen: which side each driver and truck sits on. */}
            <Route path="/dashboard/settings/sides" element={
              <ProtectedRoute><SidesPage /></ProtectedRoute>
            } />
            {/* The super_admin's queue of edits and deletions others asked for. */}
            <Route path="/dashboard/settings/change-requests" element={
              <ProtectedRoute><ChangeRequestsPage /></ProtectedRoute>
            } />
            {/* The trail eleven modules already write: who changed what. */}
            <Route path="/dashboard/settings/audit" element={
              <ProtectedRoute><AuditLogPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/change-password" element={
              <ProtectedRoute><ChangePasswordPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/settings/tv-signins" element={
              <ProtectedRoute><TvSignInsPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/settings/users" element={
              <ProtectedRoute><UsersPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/settings/users/:id" element={
              <ProtectedRoute><UserDetailPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/settings/permissions" element={
              <ProtectedRoute><PermissionsExplorerPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/trips/auto-detected" element={
              <ProtectedRoute><AutoDetectedTripsPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/settings/trip-detection" element={
              <ProtectedRoute><TripDetectionSettingsPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/investors" element={
              <ProtectedRoute><InvestorsPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/investor-rate-conflicts" element={
              <ProtectedRoute><RateConflictsPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/vehicles" element={
              <ProtectedRoute><VehiclesPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/fleet/control-tower" element={<ControlTowerPage />} />
            <Route path="/dashboard/fleet/explorer" element={
              <ProtectedRoute><FleetExplorerPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/fleet/live" element={
              <ProtectedRoute><FleetLivePage /></ProtectedRoute>
            } />
            <Route path="/dashboard/imports/vehicles" element={
              <ProtectedRoute><VehicleImportPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/imports/drivers" element={
              <ProtectedRoute><DriverImportPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/vehicles/bodies" element={
              <ProtectedRoute><BodiesPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/drivers" element={
              <ProtectedRoute><DriversPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/clients" element={
              <ProtectedRoute><ClientsPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/sites" element={
              <ProtectedRoute><SitesPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/contracts" element={
              <ProtectedRoute><ContractsPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/audit/photos" element={<ProtectedRoute><PhotoAuditPage /></ProtectedRoute>} />
          <Route path="/dashboard/payments" element={
              <ProtectedRoute><PaymentsPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/trips" element={
              <ProtectedRoute><TripsPage /></ProtectedRoute>
            } />
                        <Route path="/dashboard/ocr-review" element={
              <ProtectedRoute><OcrReviewPage /></ProtectedRoute>
            } />
            <Route path="/dashboard/ocr-settings" element={
              <ProtectedRoute><OcrSettingsPage /></ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard/settings/zoho" element={<ZohoSettingsPage />} />
                    <Route path="/dashboard/settings/fleetplus" element={<FleetPlusSettingsPage />} />
                    <Route path="/dashboard/fuel" element={<FuelPage />} />
                    <Route path="/dashboard/fuel/consumption" element={<ConsumptionPage />} />
                    <Route path="/dashboard/fuel/idle-waste" element={<IdleWastePage />} />
                    <Route path="/dashboard/fuel/driver-leaderboard" element={<DriverLeaderboardPage />} />
                    <Route path="/dashboard/fuel/coaching" element={<CoachingPage />} />
                    <Route path="/dashboard/fuel/monthly-reports" element={<MonthlyReportsPage />} />
                    <Route path="/dashboard/documents/expiry" element={<ExpiryDashboardPage />} />
                    <Route path="/dashboard/documents/packs" element={<PackBuilderPage />} />
                    <Route path="/dashboard/settings/company-documents" element={<CompanyDocumentsPage />} />
                    <Route path="/dashboard/operations-live" element={<OperationsLivePage />} />
                    <Route path="/dashboard/closings/today/:driverId" element={<DailyClosingPage />} />
                    <Route path="/dashboard/closings/admin" element={<ClosingsAdminPage />} />
                    <Route path="/dashboard/maintenance" element={<MaintenancePage />} />
            {/* Inspections: the fortnightly sheet and the extraordinary one. */}
            <Route path="/dashboard/inspections" element={
              <ProtectedRoute><InspectionsPage /></ProtectedRoute>
            } />
            {/* Read-only: what the tracker and the fuel cards know. */}
            <Route path="/dashboard/vehicle-condition" element={
              <ProtectedRoute><VehicleConditionPage /></ProtectedRoute>
            } />
                    <Route path="/dashboard/analytics/vehicles" element={<VehicleHeatmapPage />} />
                    <Route path="/dashboard/reports" element={<ReportsHomePage />} />
                    <Route path="/dashboard/reports/project" element={<ProjectReportsPage />} />
                    <Route path="/dashboard/reports/sites/:siteId/:date" element={<DailySiteReportPage />} />
                    <Route path="/dashboard/settings/report-recipients" element={<ReportRecipientsPage />} />
                    <Route path="/dashboard/activity" element={<ActivityFeedPage />} />
                    <Route path="/dashboard/reports/custom" element={<CustomReportPage />} />
                    <Route path="/dashboard/settings/afaqy-accounts" element={<AfaqyAccountsPage />} />
                    <Route path="/dashboard/projects" element={<ProjectsListPage />} />
                    <Route path="/dashboard/projects/:id" element={<ProjectDetailPage />} />
                    <Route path="/dashboard/settings/trip-rates" element={<TripRateCardsPage />} />
                    <Route path="/dashboard/cashflow/advances" element={<DriverAdvancesPage />} />
                    <Route path="/dashboard/driver-expenses" element={<DriverExpensesPage />} />
                    <Route path="/dashboard/petroapp" element={<PetroAppPage />} />
                    <Route path="/dashboard/cashflow/overdue" element={<OverdueCollectionsPage />} />
                    {/* PR MAY10 P4C — Maintenance */}
                    <Route path="/dashboard/maintenance" element={<MaintenanceListPage />} />
                    <Route path="/dashboard/maintenance/create" element={<MaintenanceEventCreatePage />} />
                    <Route path="/dashboard/maintenance/pending-verification" element={<PendingVerificationPage />} />
                    <Route path="/dashboard/maintenance/pending-approval" element={<PendingApprovalPage />} />
                    <Route path="/dashboard/maintenance/pending-payment" element={<PendingPaymentPage />} />
                    <Route path="/dashboard/maintenance/parts" element={<SparePartsCatalogPage />} />
                    <Route path="/dashboard/maintenance/inventory" element={<InventoryPage />} />
                    <Route path="/dashboard/maintenance/vendors" element={<MaintenanceVendorsPage />} />
                    <Route path="/dashboard/maintenance/schedule" element={<ServiceSchedulePage />} />
                    <Route path="/dashboard/maintenance/reports" element={<MaintenanceReportsPage />} />
                    <Route path="/dashboard/maintenance/:id" element={<MaintenanceEventDetailPage />} />
                    <Route path="/dashboard/flatbed" element={<FlatbedDashboardPage />} />
                    <Route path="/dashboard/flatbed/estimate" element={<FlatbedEstimatePage />} />
                    <Route path="/dashboard/flatbed/driver-pay" element={<FlatbedDriverPayPage />} />
                    <Route path="/dashboard/flatbed/new" element={<NewFlatbedTripPage />} />
                    <Route path="/dashboard/flatbed/my-trips" element={<MyFlatbedTripsPage />} />
                    <Route path="/dashboard/flatbed/my-trips/:tripId" element={<DriverTripActionPage />} />
                    <Route path="/dashboard/flatbed/driver-accounts" element={<DriverAccountsPage />} />
                    <Route path="/dashboard/flatbed/driver-accounts/:driverId" element={<DriverFinanceDetailPage />} />
                    <Route path="/dashboard/flatbed/salary-runs" element={<MonthlySalaryRunsPage />} />
                    <Route path="/dashboard/flatbed/my-account" element={<MyAccountPage />} />
                    <Route path="/dashboard/flatbed/cash-flow" element={<CashFlowPage />} />
                    <Route path="/dashboard/flatbed/clients/:id/payment-terms" element={<ClientPaymentTermsPage />} />
                    <Route path="/dashboard/flatbed/:tripId" element={<FlatbedTripDetailPage />} />
                    <Route path="/dashboard/drivers/:id/performance" element={<DriverPerformancePage />} />
                    <Route path="/dashboard/fuel/import" element={<FuelImportWizard />} />
                    <Route path="/dashboard/fuel/card-movements" element={<CardMovementsPage />} />
                    <Route path="/dashboard/anomalies" element={<AnomaliesPage />} />
                    <Route path="/dashboard/leaderboard" element={<LeaderboardPage />} />
                    <Route path="/dashboard/expenses" element={<ExpensesPage />} />
                    <Route path="/dashboard/construction" element={<ConstructionPage />} />
                    <Route path="/dashboard/construction/projects/:projectId/attendance" element={<AttendanceFlowPage />} />
                    <Route path="/dashboard/construction/projects/:projectId/villas/:villaId" element={<VillaDetailPage />} />
                    <Route path="/dashboard/construction/projects/:projectId/expenses" element={<ExpensesListPage />} />
                    <Route path="/dashboard/construction/projects/:projectId/performance" element={<PerformanceGalleryPage />} />
                    <Route path="/dashboard/construction/supervisors" element={<SupervisorsPage />} />
                    <Route path="/dashboard/construction/projects/:projectId/fixed-costs" element={<FixedCostsPage />} />
                    <Route path="/dashboard/water-suppliers" element={<WaterSuppliersPage />} />
                    <Route path="/dashboard/water-stations" element={<WaterStationsPage />} />
          </Routes>
        </BrowserRouter>
        </CurrentProjectProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
